import {
  ExpandableConfig,
  TableRowSelection,
} from 'antd/es/table/interface'
import { Key, useState } from 'react'

import { FunctionPermission } from '@/model/function-permission'
import {
  MenuPermission,
  Role,
} from '@/model/role'

import { DataType } from '../role-to-table-data'

export default function useSetPermissionsColumns({
  role,
  setRole,
  selectedRowKeys,
  functionPermissions,
}: {
  role: Role;
  setRole: React.Dispatch<React.SetStateAction<Role>>;
  selectedRowKeys: number[];
  functionPermissions: FunctionPermission[];
}) {
  const [expandedRowKeys, setExpandedRowKeys] = useState([0])

  const rowSelection: TableRowSelection<DataType> = {
    hideSelectAll: true,
    selectedRowKeys,
    checkStrictly: false,
    selections: false,
    onChange(nextSelectedRowKeys: Key[]) {
      const menuPermissions = nextSelectedRowKeys.map((key) => ({
        menuId: key,
        dataPermission: role.menuPermissions.find((m) => m.menuId === key)
          ?.dataPermission,
      })) as MenuPermission[]

      const functionIds = new Set<number>(role.functionIds)

      const newMenuPermissions = menuPermissions.filter(m => !selectedRowKeys.includes(m.menuId))
      for (const menuPermission of newMenuPermissions) {
        const fids = functionPermissions.filter(fp => fp.menuId === menuPermission.menuId).map(fp => fp.id)
        fids.forEach(fid => functionIds.add(fid))
      }

      const removedMenuPermissions = role.menuPermissions.filter(m => !nextSelectedRowKeys.includes(
        m.menuId))
      for (const menuPermission of removedMenuPermissions) {
        const fids = functionPermissions.filter(fp => fp.menuId === menuPermission.menuId).map(fp => fp.id)
        fids.forEach(fid => functionIds.delete(fid))
      }

      const updatedFunctionIds = Array.from(functionIds)

      setRole((role) => {
        return { ...role, menuPermissions, functionIds: updatedFunctionIds }
      })
    },
  }

  const expandable: ExpandableConfig<DataType> = {
    expandedRowKeys: expandedRowKeys,
    onExpand: (expanded, record) => {
      if (expanded) {
        setExpandedRowKeys((keys) => [...keys, record.key])
      } else {
        setExpandedRowKeys((keys) => keys.filter((k) => k !== record.key))
      }
    },
  }

  return { expandable, rowSelection }
}
